<script setup lang="ts">
import CreationFilter from '@/components/Menus/Filters/CreationFilter.vue';
import { type MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import Chip from 'primevue/chip';
import MultiSelect from 'primevue/multiselect';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const dishesStore = ref(useDishesStore());

const allergens = computed(() => [
  { name: t('creationFilters.allergensList.milk'), value: 'milk' },
  { name: t('creationFilters.allergensList.eggs'), value: 'eggs' },
  { name: t('creationFilters.allergensList.fish'), value: 'fish' },
  { name: t('creationFilters.allergensList.crustaceans'), value: 'crustaceans' },
  { name: t('creationFilters.allergensList.molluscs'), value: 'molluscs' },
  { name: t('creationFilters.allergensList.treeNuts'), value: 'treeNuts' },
  { name: t('creationFilters.allergensList.peanuts'), value: 'peanuts' },
  { name: t('creationFilters.allergensList.gluten'), value: 'gluten' },
  { name: t('creationFilters.allergensList.soybeans'), value: 'soybeans' },
  { name: t('creationFilters.allergensList.celery'), value: 'celery' },
  { name: t('creationFilters.allergensList.mustard'), value: 'mustard' },
  { name: t('creationFilters.allergensList.sesame'), value: 'sesame' },
  { name: t('creationFilters.allergensList.sulfites'), value: 'sulfites' },
  { name: t('creationFilters.allergensList.lupin'), value: 'lupin' },
]);

const selectedAllergens = computed({
  get: () => dishesStore.value.generationParams.allergens,
  set: (value) => {
    dishesStore.value.generationParams.allergens = value;
  },
});

const selectedAllergensDisplay = computed(() => {
  return selectedAllergens.value.map((allergenValue) => {
    const allergen = allergens.value.find((a) => a.value === allergenValue);
    return allergen ? allergen : { name: allergenValue, value: allergenValue };
  });
});

const removeAllergen = (allergenToRemove: string) => {
  selectedAllergens.value = selectedAllergens.value.filter((allergen) => allergen !== allergenToRemove);
};
</script>

<template>
  <CreationFilter>
    <template #title>{{ t('creationFilters.allergens') }}</template>
    <template #content>
      <div>
        <p class="mb-4 mt-0 text-sm italic">{{ t('creationFilters.allergensDescription') }}</p>
      </div>
      <MultiSelect
        v-model="selectedAllergens"
        :options="allergens"
        option-label="name"
        option-value="value"
        :placeholder="t('creationFilters.allergensDescription')"
        class="w-full"
        :max-selected-labels="0"
        :selected-items-label="t('creationFilters.allergensSelected', { count: selectedAllergens.length })"
      />
      <div v-if="selectedAllergens.length > 0" class="mt-4 flex flex-row flex-wrap gap-2">
        <Chip
          v-for="allergen in selectedAllergensDisplay"
          :key="allergen.value"
          removable
          :pt="{
            root: {
              class: 'inline-flex items-center rounded-2xl gap-2 px-3 bg-red-100 text-red-900',
            },
          }"
          @remove="removeAllergen(allergen.value)"
        >
          <template #default>
            <div class="mx-0 my-1.5 text-xs leading-4">{{ allergen.name }}</div>
          </template>
        </Chip>
      </div>
    </template>
  </CreationFilter>
</template>
